import type {
  PayloadPutMerchflowStorePog_storePogId_Notes,
  ResponseMerchflowStorePog_storePogId_Notes,
} from "@CommonApi/merchflow/store/pog/_storePogId_/notes";
import { RouteMerchflowStorePog_storePogId_Notes } from "@CommonApi/merchflow/store/pog/_storePogId_/notes";
import { routeToUrl } from "@CommonUtils/routes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import { API, getRequest, putRequest } from "src/utils/axios";

export const STORE_POG_NOTES_QUERY_KEY = ["merchflows", "storePogNotes"];

export const storePogNotesQueryKey = (storePogId: number) => [
  ...STORE_POG_NOTES_QUERY_KEY,
  storePogId,
];

const storePogNotesUrl = (storePogId: number) =>
  routeToUrl(`${API}${RouteMerchflowStorePog_storePogId_Notes}`, { storePogId });

const fetchStorePogNotes = async (storePogId: number, signal?: AbortSignal) =>
  (
    await getRequest<ResponseMerchflowStorePog_storePogId_Notes>(storePogNotesUrl(storePogId), {
      signal,
    })
  ).data;

export const useStorePogNotesQuery = (storePogId: number) =>
  useQuery({
    queryKey: storePogNotesQueryKey(storePogId),
    queryFn: ({ signal }) => fetchStorePogNotes(storePogId, signal),
    retry: false,
    refetchOnWindowFocus: false,
  });

interface SaveStorePogNotesVariables {
  storePogId: number;
  payload: PayloadPutMerchflowStorePog_storePogId_Notes;
}

const saveStorePogNotes = async ({ storePogId, payload }: SaveStorePogNotesVariables) =>
  (
    await putRequest<ResponseMerchflowStorePog_storePogId_Notes>(storePogNotesUrl(storePogId), {
      payload,
    })
  ).data;

export const useSaveStorePogNotesMutation = () => {
  const { toast } = useToasts();
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveStorePogNotes,
    // Every version of a store's POG shares one note, so other store POG ids hold it too.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORE_POG_NOTES_QUERY_KEY });
    },
    onError: (error) => toast({ title: t("storePogNotes.saveFailed"), error }),
  });
};
